import { useEffect, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import Button from "src/components/Button";
import Card from "src/components/Card";
import Chat from "src/components/chat/Chat";
import ReviewForm from "src/components/forms/ReviewForm";
import ChatButton from "src/components/forms/TaskForm/elements/ChatButton";
import RegistrationAcceptModal from "src/components/forms/TaskForm/modals/RegistrationAcceptModal";
import RegistrationDenieModal from "src/components/forms/TaskForm/modals/RegistrationDenieModal";
import Tabs from "src/components/layout/Tabs";
import Resume from "src/components/resume/Resume";
import useTask from "src/hooks/api/tasks/useTask";
import useGet from "src/hooks/rest/useGet";
import useAuth from "src/hooks/selectors/useAuth";
import useSocketListener from "src/hooks/sockets/useSocketListener";
import useQueryString from "src/hooks/useQueryString";
import RegistrationButtons from "src/components/tasks/panes/registration/RegistrationButtons";
import RegistrationDetails from "src/components/tasks/panes/registration/RegistrationDetails";
import RegistrationHead from "src/components/tasks/panes/registration/RegistrationHead";
import RegistrationMatchDetails from "src/components/tasks/panes/registration/RegistrationMatchDetails";
import RegistrationReviews from "src/components/tasks/panes/registration/RegistrationReviews";
import { useTranslation } from "react-i18next";

interface SelectedRegistrationPaneProps {
	id: number | string;
}

const SelectedRegistrationPane = ({ id }: SelectedRegistrationPaneProps) => {
	const { t } = useTranslation("registrations", {
		keyPrefix: "selected-registration-pane",
	});
	const { extra } = useParams() as any;
	const [{ data: registration }, , { fetchGet }] = useGet(
		`/company/registrations/${id}?with[]=resume`
	);
	const [matchTries, setMatchTries] = useState(0);
	const location = useLocation();
	const navigate = useNavigate();
	const { task, actions } = useTask();
	const auth = useAuth();
	const { status, user } = registration || {};
	const tabs = {
		matched: [t("tabs.match"), t("tabs.registration"), t("tabs.reviews")],
		pending: [t("tabs.registration")],
	};

	if (user?.has_resume) {
		tabs.pending.push(t("tabs.resume"));
	}

	const qs = useQueryString();

	useSocketListener(
		registration?.payment && `payment.${registration?.payment?.hid}.status`,
		() => {
			fetchGet(false);
		}
	);

	useEffect(() => {
		if (qs.payment_return && qs.rid && registration?.id) {
			if (registration?.status !== "matched") {
				const interval = setInterval(() => {
					fetchGet();
					setMatchTries((i) => i + 1);
				}, 3000);

				if (matchTries > 11) {
					navigate(location.pathname);
				}
				return () => clearInterval(interval);
			} else {
				navigate(location.pathname);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [qs, registration]);

	const onClose = async () => {
		fetchGet(false);
		navigate(`/tasks/${task?.hid}/show/${registration?.hid}`);
		actions.get(true);
	};

	if (registration && task && task?.hid !== registration?.task?.hid) {
		return (
			<div className="flex flex-col flex-1 justify-center items-center">
				<strong>{t("empty.title")}</strong>
			</div>
		);
	}

	if (qs?.payment_return) {
		return (
			<div className="flex flex-col">
				<Card type="no-padding">
					<div className="flex flex-col gap-6">
						{registration && (
							<RegistrationHead {...{ registration }} />
						)}
						<div className="p-12 flex flex-col justify-center items-center gap-6">
							<span className="base-loader"></span>
						</div>
					</div>
				</Card>
			</div>
		);
	}

	if (extra && extra === "chat" && registration?.id && task?.id) {
		return (
			<Chat
				options={{
					type: "task",
					room: `room-${registration.id}`,
					backUrl: `/tasks/${task?.hid}/show/${registration.hid}`,
				}}
				data={{
					task,
					registration,
				}}
				loading={false}
			/>
		);
	}

	if (
		(extra === "review" || extra === "cancel") &&
		registration?.id &&
		task?.id
	) {
		if (
			extra === "review" &&
			!["reserved", "completed"]?.includes(registration?.payment?.status)
		) {
			return (
				<Card title={t("not-finishable.title")}>
					<p>{t("not-finishable.description")}</p>
					<div className="flex mt-2">
						<Button
							type="gray"
							to={`/tasks/${id}/show/${registration?.hid}`}
						>
							{t("not-finishable.button")}
						</Button>
					</div>
				</Card>
			);
		}

		return (
			<div className="flex flex-col gap-8">
				<ReviewForm
					taskId={task.id}
					userId={registration?.user?.id}
					companyId={task?.company?.id}
					registrationId={registration?.id}
					onDone={() => {
						fetchGet(false);
						navigate(
							`/tasks/${task?.hid}/show/${registration?.hid}`
						);
					}}
					variant={extra === "cancel" ? "cancel" : undefined}
					onCancel={() =>
						navigate(
							`/tasks/${task?.hid}/show/${registration?.hid}`
						)
					}
				/>
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-8">
			{registration?.id && (
				<>
					{auth?.has_subscription &&
						registration.status === "pending" && (
							<>
								<RegistrationDenieModal
									visible={extra === "denie"}
									registrationId={id}
									{...{ onClose }}
								/>
								<RegistrationAcceptModal
									visible={extra === "accept"}
									{...{ registration, task }}
									{...{ onClose }}
								/>
							</>
						)}
					<Card type="no-padding">
						<div className="flex flex-col gap-6">
							<RegistrationHead {...{ registration }} />
							<div className="px-6 pb-6 flex flex-col gap-6">
								<hr className="border-dark opacity-10" />
								<Tabs
									options={{
										noBackground: true,
										scrollToActive: true,
										small: true,
									}}
									tabs={
										[
											"matched",
											"completed",
											"canceled",
										].includes(status)
											? tabs.matched
											: tabs.pending
									}
									spacing={{
										wrapper: {
											top: 4,
										},
									}}
									actions={{
										right: (
											<ChatButton
												className="gtm-company-dashboard-opdracht--button-naar-chat"
												to={`/tasks/${task.hid}/show/${registration?.hid}/chat`}
												registrationId={
													registration?.id
												}
											/>
										),
									}}
									header={
										<hr className="border-dark opacity-10 mt-6" />
									}
								>
									{(tab) => {
										if (tab === t("tabs.match")) {
											return (
												<>
													<RegistrationMatchDetails
														{...{
															registration,
														}}
													/>
												</>
											);
										}
										if (tab === t("tabs.reviews")) {
											return (
												<RegistrationReviews
													{...{
														registration,
													}}
												/>
											);
										}

										if (tab === t("tabs.resume")) {
											return (
												<>
													<Resume
														id={user?.id}
														exclude={["header"]}
														variant="registration"
													/>
												</>
											);
										}

										return (
											<RegistrationDetails
												{...{ registration }}
											/>
										);
									}}
								</Tabs>
								<hr className="border-dark opacity-10" />
								<RegistrationButtons {...{ registration }} />
							</div>
						</div>
					</Card>
				</>
			)}
		</div>
	);
};

export default SelectedRegistrationPane;
